import { ArrowDownTrayIcon, ChevronRightIcon, DocumentIcon } from "@heroicons/react/24/outline";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import eventIcon from "../../../assets/opportunityPages/contractProgress/eventIcon.svg";
import fundsIcon from "../../../assets/opportunityPages/contractProgress/fundsIcon.svg";
import payoutIcon from "../../../assets/opportunityPages/contractProgress/payoutIcon.svg";
import resultIcon from "../../../assets/opportunityPages/contractProgress/resultIcon.svg";
import signatureIcon from "../../../assets/opportunityPages/contractProgress/signatureIcon.svg";
import { OpportunityCardResponseDtoManagerSummary } from "../../../types/types";
import {
  EMPTY_VALUE,
  OpportunityFormData,
  formatDisplayDate,
  getFileExtension,
  getFileName,
  getFileUrl,
  safe,
  toTitleCase,
} from "./opportunityViewHelpers";

export const CARD_SHADOW_CLASS =
  "shadow-[0px_0px_1px_0px_rgba(0,0,0,0.06),0px_1px_2px_0px_rgba(0,0,0,0.06),0px_2px_4px_0px_rgba(0,0,0,0.04)]";

interface DeadlineRule {
  stage?: string;
  offsetDays?: number;
  offsetHours?: number;
  referencePoint?: string;
  description?: string;
}

interface ProgressStage {
  key: string;
  title?: string;
  offset: string;
  subtitle: string;
  icon: string;
}

const EVENT_STAGE = "EVENT";
const PROGRESS_COLUMNS = 5;

// Display order of the lifecycle. Stage names, offsets and descriptions all
// come from the backend template (deadlineRules); this only orders the cards.
const STAGE_ORDER = [
  "ACCEPTED",
  "CONFIRMED",
  "PRE_PAYMENT",
  EVENT_STAGE,
  "RESULT_UPDATED",
  "RESULT_CONFIRMED",
  "POST_PAYMENT",
  "FIGHTER_RATED",
  "MANAGER_RATED",
];

const STAGE_ICONS: Record<string, string> = {
  ACCEPTED: signatureIcon,
  CONFIRMED: signatureIcon,
  PRE_PAYMENT: fundsIcon,
  [EVENT_STAGE]: eventIcon,
  RESULT_UPDATED: resultIcon,
  RESULT_CONFIRMED: resultIcon,
  POST_PAYMENT: payoutIcon,
  FIGHTER_RATED: resultIcon,
  MANAGER_RATED: resultIcon,
};

// Icon ids come from contractProgress[].icon in the template JSON.
const CONTRACT_PROGRESS_ICONS: Record<string, string> = {
  document_sign: signatureIcon,
  safe: fundsIcon,
  glove: eventIcon,
  handshake: resultIcon,
  payment: payoutIcon,
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// The backend template already sends the display steps (signing, escrow,
// event, result, payout) in order. Show exactly what it sends.
const getTemplateProgress = (templateJson: unknown): ProgressStage[] => {
  if (!isPlainObject(templateJson) || !Array.isArray(templateJson.contractProgress)) {
    return [];
  }

  return templateJson.contractProgress.filter(isPlainObject).map((step) => ({
    key: String(step.id ?? ""),
    title: String(step.title ?? ""),
    offset: "",
    subtitle: step.subtitle ? String(step.subtitle) : "",
    icon: CONTRACT_PROGRESS_ICONS[String(step.icon ?? "")] ?? resultIcon,
  }));
};

const getDeadlineRules = (templateJson: unknown): DeadlineRule[] => {
  if (!isPlainObject(templateJson) || !Array.isArray(templateJson.steps)) {
    return [];
  }
  const step = templateJson.steps.find(
    (item: unknown) =>
      isPlainObject(item) && item.stepId === "deadlineRules",
  );
  if (!isPlainObject(step) || !Array.isArray(step.deadlineRules)) return [];
  return step.deadlineRules.filter(isPlainObject) as DeadlineRule[];
};

// "2d 12h", "14d", "12h". Sign only when the rule is relative to the event.
const formatOffset = (rule: DeadlineRule): string => {
  const days = Number(rule.offsetDays) || 0;
  const hours = Number(rule.offsetHours) || 0;
  const parts = [days ? `${days}d` : "", hours ? `${hours}h` : ""].filter(
    Boolean,
  );
  if (parts.length === 0) return "";
  const sign =
    rule.referencePoint === "PRE_PAYMENT_TIME"
      ? "-"
      : rule.referencePoint === "POST_PAYMENT_TIME"
        ? "+"
        : "";
  return `${sign}${parts.join(" ")}`;
};

const getStageRank = (stage: string): number => {
  const index = STAGE_ORDER.indexOf(stage);
  return index === -1 ? STAGE_ORDER.length : index;
};

const buildProgressStages = (
  templateJson: unknown,
  formData?: OpportunityFormData,
): ProgressStage[] => {
  const templateStages = getTemplateProgress(templateJson);
  if (templateStages.length > 0) return templateStages;

  const stages: ProgressStage[] = getDeadlineRules(templateJson)
    .filter((rule) => !!rule.stage)
    .map((rule) => ({
      key: String(rule.stage),
      offset: formatOffset(rule),
      subtitle: rule.description ? String(rule.description) : "",
      icon: STAGE_ICONS[String(rule.stage)] ?? resultIcon,
    }));

  const details = formData?.details;
  const eventDate =
    isPlainObject(details) && typeof details.eventDate === "string"
      ? details.eventDate
      : "";
  if (eventDate) {
    stages.push({
      key: EVENT_STAGE,
      offset: "",
      subtitle: formatDisplayDate(eventDate),
      icon: eventIcon,
    });
  }

  return stages
    .map((stage, index) => ({ stage, index }))
    .sort(
      (a, b) =>
        getStageRank(a.stage.key) - getStageRank(b.stage.key) ||
        a.index - b.index,
    )
    .map(({ stage }) => stage);
};

export const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="self-stretch border-b border-gray-200 pb-0.5">
    <h3 className="text-base font-medium leading-6 text-gray-500">{title}</h3>
  </div>
);

export const Section: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => (
  <section className="flex flex-col gap-2">
    <SectionHeader title={title} />
    {description && (
      <p className="text-sm font-normal leading-5 text-gray-500">{description}</p>
    )}
    {children}
  </section>
);

export const InfoPair: React.FC<{
  label: string;
  value: React.ReactNode;
  wide?: boolean;
}> = ({ label, value, wide }) => (
  <div className={`flex min-w-0 flex-col ${wide ? "col-span-2" : ""}`}>
    <p className="pr-2 text-sm font-normal leading-5 text-gray-500">{label}</p>
    <div className="break-words text-base font-medium leading-6 text-gray-900">
      {value ?? EMPTY_VALUE}
    </div>
  </div>
);

export const FileValue: React.FC<{ label: string; value: unknown }> = ({
  label,
  value,
}) => {
  const { t } = useTranslation("opportunities");
  const fileUrl = getFileUrl(value);
  const fileName = getFileName(value, label || t("document"));
  const extension = getFileExtension(fileName, fileUrl);

  const handleDownload = async () => {
    if (!fileUrl) return;

    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error(t("download_failed"));

      const objectUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error(t("download_failed"));
    }
  };

  return (
    <div
      className={`flex w-full min-w-0 items-center gap-3 rounded-3xl bg-white p-4 ring-1 ring-black/5 ${CARD_SHADOW_CLASS}`}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
        <DocumentIcon className="h-4 w-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col pr-6">
        <span
          className="block truncate text-sm font-medium leading-5 text-gray-900"
          title={fileName}
        >
          {fileName}
        </span>
        <span className="text-xs font-normal uppercase leading-4 text-gray-500">
          {extension}
        </span>
      </span>
      <button
        type="button"
        onClick={handleDownload}
        className="flex h-9 w-9 min-w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-900 transition hover:bg-gray-200"
        title={t("download_file", { fileName })}
        aria-label={t("download_file", { fileName })}
      >
        <ArrowDownTrayIcon className="h-4 w-4" />
      </button>
    </div>
  );
};

// Wraps only at spaces: hyphenated words ("Pre-match") stay on one line.
const WrapAtSpaces: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text
      .split(/\s+/)
      .filter(Boolean)
      .map((word, index) => (
        <React.Fragment key={index}>
          {index > 0 && " "}
          {word.includes("-") ? (
            <span className="whitespace-nowrap">{word}</span>
          ) : (
            word
          )}
        </React.Fragment>
      ))}
  </>
);

export const ContractProgress: React.FC<{
  templateJson?: unknown;
  formData?: OpportunityFormData;
}> = ({ templateJson, formData }) => {
  const { t } = useTranslation("opportunities");
  const stages = buildProgressStages(templateJson, formData);

  if (stages.length === 0) return null;

  return (
    <Section title={t("opportunity_view.sections.contractProgress")}>
      <div className="grid grid-cols-5 gap-x-4 gap-y-3 pt-1">
        {stages.map((stage, index) => {
          const translatedLabel = t(`opportunity_view.progress.stages.${stage.key}`, {
            defaultValue: toTitleCase(stage.key),
          });
          const label = stage.title || translatedLabel;
          const showChevron =
            index < stages.length - 1 && (index + 1) % PROGRESS_COLUMNS !== 0;

          return (
            <div key={`${stage.key}-${index}`} className="relative min-w-0">
              <div
                className="flex h-full min-w-0 flex-col items-center justify-between gap-3.5 rounded-[20.83px] p-2.5 text-center outline outline-[0.87px] outline-offset-[-0.87px] outline-gray-200 bg-white"
              >
                <div className="flex flex-col items-start w-full">
                  <span className="w-full break-words text-balance text-center text-[10.41px] font-bold leading-3 text-gray-900">
                    <WrapAtSpaces text={label} />
                    {stage.offset && (
                      <>
                        {" "}
                        <span className="whitespace-nowrap">({stage.offset})</span>
                      </>
                    )}
                  </span>
                  {stage.subtitle && (
                    <span className="w-full break-words text-balance text-center text-[10.41px] font-normal leading-3 text-gray-500">
                      <WrapAtSpaces text={stage.subtitle} />
                    </span>
                  )}
                </div>
                <img src={stage.icon} alt="" className="h-16 w-16 object-contain" />
              </div>
              {showChevron && (
                <ChevronRightIcon
                  className="pointer-events-none absolute -right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  strokeWidth={2}
                />
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
};

// The summary has no nickname field today; it is used only if the backend sends one.
type ManagerWithNickname = OpportunityCardResponseDtoManagerSummary & {
  nickname?: string;
};

export const ManagerPanel: React.FC<{
  manager?: ManagerWithNickname;
  onProfileOpen: (profileId?: string | number | null) => void;
}> = ({ manager, onProfileOpen }) => {
  const { t } = useTranslation("opportunities");
  // Short name: nickname if there is one, otherwise just the first name.
  const displayName = safe(manager?.nickname?.trim() || manager?.firstName?.trim());
  // Manager role is being phased out: when there is no organization name,
  // show the organizer's short name instead of "-".
  const organizationLabel = safe(
    manager?.organizationName?.trim() ||
      manager?.nickname?.trim() ||
      manager?.firstName?.trim(),
  );
  const initials =
    manager?.firstName || manager?.lastName
      ? `${(manager?.firstName || "").charAt(0)}${(manager?.lastName || "").charAt(0)}`
      : "?";

  return (
    <div className="flex w-full flex-col rounded-3xl bg-gray-100">
      <button
        type="button"
        onClick={() => onProfileOpen(manager?.id)}
        disabled={!manager?.id}
        className="flex w-full flex-col text-left focus:outline-none disabled:cursor-default"
      >
        <div className="flex h-[276px] w-full p-1">
          {manager?.profileImage ? (
            <img
              src={manager.profileImage}
              alt={displayName}
              className="h-full w-full rounded-[20px] object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-[20px] bg-gray-300 text-5xl font-bold text-white">
              {initials}
            </div>
          )}
        </div>

        <div className="flex flex-col px-4 pt-2">
          <h4 className="truncate text-2xl font-bold leading-8 text-gray-900">
            {displayName}
          </h4>
        </div>
      </button>

      <div className="grid grid-cols-2 gap-x-4 gap-y-4 p-4">
        <InfoPair
          label={t("opportunity_view.fields.organizationName")}
          value={<span className="line-clamp-1 break-all">{organizationLabel}</span>}
        />
        <InfoPair
          label={t("opportunity_view.fields.nationality")}
          value={safe(manager?.nationality)}
        />
        <InfoPair
          label={t("opportunity_view.fields.gender")}
          value={
            manager?.gender
              ? toTitleCase(manager.gender.toLowerCase())
              : EMPTY_VALUE
          }
        />
        <InfoPair
          label={t("opportunity_view.fields.residenceCountry")}
          value={safe(manager?.residenceCountry)}
        />
      </div>
    </div>
  );
};